import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'antd'
import TextArea from 'antd/es/input/TextArea'
import { CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { useThemeColors } from '../../../../../../../../../../hooks/useThemeColors'

const useSyncValue = initialValue => {
    const [value, setValue] = useState('')

    useEffect(() => {
        setValue(initialValue || '')
    }, [initialValue])

    return [value, setValue]
}

const ManualSaveTextArea = ({
    initialValue,
    onSave,
    isLoading,
    readonly,
    placeholder,
    className,
    showButtons = true,
    onFocus,
    onBlur,
    onValueChange,
    /** When `key` changes, replaces the textarea value (does not persist until save). */
    applyDraft,
    ...textAreaProps
}) => {
    const colors = useThemeColors()
    const [value, setValue] = useSyncValue(initialValue)
    const [isFocused, setIsFocused] = useState(false)
    const lastAppliedDraftKeyRef = useRef(null)

    useEffect(() => {
        const k = applyDraft?.key
        if (k == null || k === lastAppliedDraftKeyRef.current) return
        lastAppliedDraftKeyRef.current = k
        const next = applyDraft.text ?? ''
        setValue(next)
        onValueChange?.(next)
        // Intentionally only `key`: new drafts bump key; `text` is read once per bump.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyDraft?.key])

    const handleBlur = () => {
        setIsFocused(false)
        onBlur?.()
    }

    const handleFocus = () => {
        setIsFocused(true)
        onFocus?.()
    }

    const handleChange = event => {
        if (readonly) return
        const newValue = event.target.value
        setValue(newValue)
        onValueChange?.(newValue)
    }

    const handleSave = () => {
        onSave(value)
    }

    const handleCancel = () => {
        const resetValue = initialValue || ''
        setValue(resetValue)
        onValueChange?.(resetValue)
    }

    const hasValue = value.length > 0
    const hasChanges = value.trim() !== (initialValue || '').trim()

    const shouldShowButtons = !readonly && showButtons && hasChanges

    return (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <TextArea
                value={value}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleFocus}
                autoSize
                placeholder={hasValue || isFocused ? '' : placeholder}
                disabled={isLoading}
                readOnly={readonly}
                className={className}
                bordered={false}
                data-test={textAreaProps['data-test']}
                {...textAreaProps}
            />
            {shouldShowButtons && (
                <div 
                    style={{ 
                        marginTop: 4, 
                        display: 'flex', 
                        gap: '4px',
                        justifyContent: 'flex-end'
                    }}
                >
                    <Button
                        icon={<CheckOutlined />}
                        onClick={handleSave}
                        loading={isLoading}
                        disabled={isLoading}
                        size='small'
                        title='Save'
                        style={{ color: colors.success, borderColor: colors.success }}
                        data-test='save-textarea-button'
                    />
                    <Button
                        icon={<CloseOutlined />}
                        onClick={handleCancel}
                        disabled={isLoading}
                        size='small'
                        title='Cancel'
                        danger
                        data-test='cancel-textarea-button'
                    />
                </div>
            )}
        </div>
    )
}

export default ManualSaveTextArea

